import BaseLayout from '@/layout/BaseLayout';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useForm } from 'react-hook-form';

import { trpc } from '@/trpcClient';
import { checkRoleEditInput, type EditRoleInputType } from '@shelflix/schemas/role/edit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { EPermissionCategory } from '@shelflix/schemas/role/enums';
import Icon from '@shelflix/ui-web/components/Icon';
import { Checkbox } from '@shelflix/ui-web/components/ui/checkbox';
import { toast } from 'sonner';
import { useRoleFormStore } from './store';
import { PermissionCheckbox } from '@/components/PermissionCheckbox';

// Define the order of categories as per the spreadsheet
const CATEGORY_ORDER: EPermissionCategory[] = [
  'Materiais de PDV' as EPermissionCategory,
  'Campanhas' as EPermissionCategory,
  'Mídias' as EPermissionCategory,
  'Relatórios' as EPermissionCategory,
  'Administrador' as EPermissionCategory,
  'Sensores de fluxo' as EPermissionCategory,
];

export function RoleEdit() {
  const { uuid: roleId } = useParams<{ uuid: string }>();

  const { data: roleToEdit, isLoading: _isLoadingRole } = useQuery(
    trpc.role.get.queryOptions(
      {
        uuid: roleId ?? '',
      },
      {
        enabled: !!roleId,
      }
    )
  );

  const { formData, resetForm } = useRoleFormStore();
  const editRoleMutation = useMutation(trpc.role.edit.mutationOptions());
  const navigate = useNavigate();

  const form = useForm<EditRoleInputType>({
    defaultValues: formData,
    mode: 'all',
  });

  useEffect(() => {
    if (roleToEdit) {
      form.reset({
        name: roleToEdit.name,
        permissions: roleToEdit.permissions,
      });
    }
  }, [roleToEdit]);

  const { data: allPermissions = [] } = useQuery(trpc.permission.list.queryOptions({}));
  const { data: dependencyMap = {} } = useQuery(trpc.permission.listDependencies.queryOptions());

  const groupedPermissions = allPermissions.reduce(
    (acc, perm) => {
      if (!acc[perm.category]) acc[perm.category] = [];
      acc[perm.category].push(perm);
      return acc;
    },
    {} as Record<EPermissionCategory, typeof allPermissions>
  );

  const onSubmit = (data: EditRoleInputType) => {
    if (editRoleMutation.isPending) return;
    const payload = {
      name: data.name,
      permissions: data.permissions,
      active: true,
    };

    if (roleToEdit) {
      editRoleMutation.mutate(
        { uuid: roleToEdit.uuid, hierarchy: roleToEdit.hierarchy, ...payload },
        {
          onSuccess: () => {
            navigate('/user', { state: { toastSuccess: true } });
            resetForm();
          },
          onError: (error) => {
            toast.error(`Erro ao editar cargo: ${error.message}`);
          },
        }
      );
    }
  };

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col">
          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                Editar perfil
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ validate: (value) => checkRoleEditInput('name', value) }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-[3px]">
                      <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome do cargo</FormLabel>
                      <FormControl>
                        <Input className="h-10" placeholder="Digite o nome do usuário" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="align-center flex gap-6">
                  <FormField
                    control={form.control}
                    name="permissions"
                    render={({ field }) => {
                      return (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">&nbsp;</FormLabel>
                          <FormControl>
                            <div className="flex flex-row items-start space-y-0 space-x-3">
                              <Checkbox
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...allPermissions.map((p) => p.uuid)])
                                    : field.onChange([]);
                                }}
                              ></Checkbox>
                              <div className="flex flex-col">
                                <span className="leading-[20px] font-[16px] font-medium text-[var(--gray-primary)]">
                                  Selecionar tudo
                                </span>
                              </div>
                            </div>
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="px-0 md:px-8">
                <FormField
                  control={form.control}
                  name="permissions"
                  render={() => (
                    <div className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
                      {CATEGORY_ORDER.filter((category) => groupedPermissions[category]).map((category) => {
                        const permissions = groupedPermissions[category].sort((a, b) => a.id - b.id);
                        return (
                          <div key={category} className="flex flex-col">
                            <div className="mb-4">
                              <span className="text-primary text-[18px] leading-5 font-semibold sm:text-xl">
                                {category}
                              </span>
                            </div>
                            {/* Only show "select all" checkbox when category has more than 1 permission */}
                            {permissions.length > 1 && (
                              <FormField
                                control={form.control}
                                name="permissions"
                                render={({ field }) => {
                                  const groupPermissionUUIDs = permissions.map((p) => p.uuid);
                                  const allSelected = groupPermissionUUIDs.every((id) => field.value?.includes(id));

                                  return (
                                    <FormItem className="mb-4 flex flex-row items-start space-y-0 space-x-3">
                                      <FormControl>
                                        <Checkbox
                                          id={`select-all-${category}`}
                                          checked={allSelected}
                                          onCheckedChange={(checked) => {
                                            const current = new Set(field.value ?? []);
                                            if (checked) {
                                              groupPermissionUUIDs.forEach((id) => current.add(id));
                                            } else {
                                              groupPermissionUUIDs.forEach((id) => current.delete(id));
                                            }
                                            field.onChange(Array.from(current));
                                          }}
                                        />
                                      </FormControl>
                                      <label
                                        htmlFor={`select-all-${category}`}
                                        className="cursor-pointer text-sm font-normal"
                                      >
                                        <div className="flex flex-col">
                                          <span className="text-base leading-[20px] font-medium text-[var(--gray-primary)]">
                                            Selecionar todas as opções
                                          </span>
                                          <span className="font-[12px] font-normal text-[var(--gray-secondary)]">
                                            Libera todos os acessos desta área
                                          </span>
                                        </div>
                                      </label>
                                    </FormItem>
                                  );
                                }}
                              />
                            )}
                            <div className="flex flex-col gap-4">
                              {permissions.map((item) => (
                                <FormField
                                  key={item.uuid}
                                  control={form.control}
                                  name="permissions"
                                  rules={{ validate: (value) => checkRoleEditInput('permissions', value) }}
                                  render={({ field }) => (
                                    <PermissionCheckbox
                                      permission={item}
                                      allPermissions={allPermissions}
                                      selectedPermissionIds={field.value ?? []}
                                      onPermissionsChange={field.onChange}
                                      dependencyMap={dependencyMap}
                                    />
                                  )}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="my-5 flex grow-0 justify-center">
            <Button
              type="submit"
              loading={editRoleMutation.isPending}
              className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
            >
              <Icon name="admin_panel_settings" color="#fff" fill={1} />
              <span className="[font-family:'Figtree'] text-sm">Salvar perfil administrativo</span>
            </Button>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
